import React from 'react';
import JsonLd from '../../components/JsonLd';
import SEOHead from '../../components/SEOHead';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import {
  KeyRound, ShieldCheck, Key, FileText, Settings, ListChecks, ArrowRight, CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const PasswordResetPage = () => {
  const benefits = [
    {
      icon: ShieldCheck,
      title: 'MFA-based secure identity verification',
      description:
        'Every reset and unlock is gated by multi-factor verification using OTP, authenticator apps, or policy-based challenge methods - no weak caller-ID-only auth.',
    },
    {
      icon: Key,
      title: 'End-to-end directory actions + ITSM orchestration',
      description:
        'The AI executes the reset or unlock directly in Azure AD, Active Directory, or Okta, and automatically creates, updates, and closes the ITSM ticket.',
    },
    {
      icon: KeyRound,
      title: '24/7 always-on with instant response',
      description:
        'Employees get immediate self-service resolution at any hour - no queue wait, no callback scheduling, no L1 agent involvement for standard cases.',
    },
  ];

  const capabilities = [
    'Integrate Azure AD / AD / Okta / Duo (MFA + directory APIs)',
    'Configure reset rules, lockout thresholds, and risk triggers',
    'Connect ITSM: ServiceNow/Jira (create/update/close + audit logging)',
    'Define flows: reset, unlock, MFA enrollment status checks',
    'RBAC + approvals + full audit trail',
  ];

  const features = [
    {
      icon: ShieldCheck,
      title: 'MFA Identity Verification',
      desc: 'Verify the requestor via OTP, push notification, or KBA before executing any directory action - configurable per risk policy and user segment.',
      gradient: 'from-indigo-500 to-violet-500',
    },
    {
      icon: Key,
      title: 'Azure AD / Okta Integration',
      desc: 'Execute password resets and account unlocks natively in Azure AD, on-prem Active Directory, Okta, and hybrid environments via secure API calls.',
      gradient: 'from-violet-500 to-purple-500',
    },
    {
      icon: FileText,
      title: 'ITSM Auto-Ticketing',
      desc: 'Auto-create the ITSM ticket on request, update it through the workflow, and close it on completion - with a full audit trail for compliance.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: Settings,
      title: 'Policy-Based Reset Rules',
      desc: 'Define which accounts are eligible for self-service, which require manager approval, and which must be escalated - by role, risk level, or directory.',
      gradient: 'from-pink-500 to-rose-500',
    },
    {
      icon: ListChecks,
      title: 'Audit Logging',
      desc: 'Every verification step, directory action, and ITSM update is logged with timestamp, actor, and outcome for security and compliance review.',
      gradient: 'from-rose-500 to-red-400',
    },
    {
      icon: ArrowRight,
      title: 'Safe Escalation',
      desc: 'When verification fails or risk thresholds are exceeded, the agent escalates to L1 with the full context - preventing unauthorized access while maintaining service.',
      gradient: 'from-orange-400 to-amber-400',
    },
  ];

  const steps = [
    {
      step: '01',
      title: 'Employee contacts IT helpdesk',
      desc: 'The employee calls the helpdesk number or initiates a Teams voice/chat interaction and identifies the need for a password reset or account unlock.',
    },
    {
      step: '02',
      title: 'AI collects identifier',
      desc: 'The agent prompts for the User Principal Name (UPN) or Employee ID to locate the account in the connected directory system.',
    },
    {
      step: '03',
      title: 'AI verifies via MFA or OTP',
      desc: 'Based on configured policy, the agent triggers an OTP to the registered device or email, or prompts a push notification via the authenticator app.',
    },
    {
      step: '04',
      title: 'AI executes password reset or account unlock',
      desc: 'On successful verification, the agent calls the directory API to reset the password or unlock the account - immediately and without agent involvement.',
    },
    {
      step: '05',
      title: 'AI creates, updates, and closes ITSM ticket',
      desc: 'The ITSM ticket is created at intake, updated at each step, and closed with resolution details once the directory action completes successfully.',
    },
    {
      step: '06',
      title: 'Employee receives confirmation',
      desc: 'The employee is notified via their preferred channel that the reset or unlock is complete, with any temporary credentials delivered securely.',
    },
  ];

  const stats = [
    { value: '~45%', label: 'Ticket deflection' },
    { value: '~40%', label: 'L1 workload reduction' },
    { value: '<$5', label: 'Cost per ticket (vs $25–$40)' },
    { value: '24/7', label: 'Instant self-service support' },
  ];

  const faqs = [
    {
      q: 'Is this secure enough for enterprise use?',
      a: 'Yes. Every action is gated by MFA verification, logged with full audit trails, and governed by configurable RBAC policies. No reset or unlock is executed without successful identity verification. All directory API calls are made over secure, encrypted channels with least-privilege service accounts.',
    },
    {
      q: 'Can it support hybrid AD and Azure AD environments?',
      a: 'Yes. Digian.ai supports hybrid environments where on-premises Active Directory is synchronized with Azure AD. The agent can perform resets in the appropriate directory based on the account type, and changes propagate through the configured sync.',
    },
    {
      q: 'What happens if verification fails?',
      a: 'If the employee cannot complete MFA, the agent does not attempt any directory action. Instead, it escalates the request to an L1 agent with the identity information collected, so the agent can apply an alternative verification process - maintaining security without leaving the employee stranded.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="useCasePasswordReset" />
      <JsonLd schema={{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Home","item":"https://digian.ai"},{"@type":"ListItem","position":2,"name":"Use Cases","item":"https://digian.ai/use-cases"},{"@type":"ListItem","position":3,"name":"Password Reset","item":"https://digian.ai/use-cases/password-reset"}]}} />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <p className="text-sm font-medium tracking-wide text-indigo-600 uppercase">IT Helpdesk / Password Reset &amp; Account Unlock</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900">
              AI Voice Password Reset &amp; Account Unlock - Secure, Instant, 24/7
            </h1>
            <p className="text-lg text-slate-600">
              Automate the highest-volume IT Helpdesk requests: password resets and account unlocks, using MFA verification and direct actions in Azure AD, Active Directory, and Okta, with ITSM ticket auto-updates and closure.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/demo"><Button size="lg">Request a Demo</Button></Link>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              {['MFA-verified every time', 'Azure AD + Okta + hybrid AD', 'ITSM auto-closed'].map(t => (
                <span key={t} className="flex items-center gap-1.5 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />{t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <KeyRound className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-indigo-600 uppercase">Password Reset Automation</p>
                <p className="text-sm text-slate-600">Secure, policy-driven, end-to-end IT self-service</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-slate-700">
              {capabilities.map((item, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <KeyRound className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-indigo-600 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <motion.div key={s.label} {...fadeUp} transition={{ delay: i * 0.1 }}>
                <p className="text-3xl font-extrabold text-white">{s.value}</p>
                <p className="text-indigo-200 text-sm mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Why IT teams automate password resets</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div key={b.title} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Capabilities</p>
            <h2 className="text-3xl font-bold text-slate-900">Everything needed to automate IT account self-service</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={f.title} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all group">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-4`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-indigo-700 transition-colors">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Process</p>
            <h2 className="text-3xl font-bold text-slate-900">How AI password reset works at runtime</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={s.step} {...fadeUp} transition={{ delay: i * 0.12 }}
                className="bg-white rounded-2xl border border-slate-200 p-8">
                <div className="flex items-center gap-4 mb-5">
                  <span className="text-5xl font-black text-indigo-100">{s.step}</span>
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <KeyRound className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">Deflect 40–50% of IT tickets starting this quarter</h2>
            <p className="text-xl text-indigo-100">
              The single highest-volume helpdesk request - automated securely, at scale, 24/7.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo"><Button size="lg" variant="secondary">Request Demo</Button></Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                  Talk to Our Team <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <p className="text-indigo-200 text-sm">MFA-secured · Azure AD + Okta + hybrid AD · ITSM auto-closed</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PasswordResetPage;
